import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRODUCT_IMAGE_LIMITS, type VehicleImage } from '@kontor/shared';
import { api, apiUpload, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import './fleet.css';

const sizeText = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`);
const photoUrl = (vehicleId: string, imageId: string) => `/api/v1/fleet/vehicles/${vehicleId}/images/${imageId}`;
const LIMIT = PRODUCT_IMAGE_LIMITS.maxCount;

/** Fotos eines Fahrzeugs wie bei Produkten: großes Hauptfoto, Vorschaubilder mit Pfeilen zum Sortieren, „Als Hauptfoto“ und Löschen. Mehrere Dateien auf einmal hochladbar. */
export function VehiclePhotos({ vehicleId, title, canWrite }: { vehicleId: string; title: string; canWrite: boolean }) {
  const client = useQueryClient();
  const key = ['fleet-vehicle-images', vehicleId];
  const list = useQuery({ queryKey: key, queryFn: async () => (await api<{ data: VehicleImage[] }>('GET', `/fleet/vehicles/${vehicleId}/images`)).data });
  const [selected, setSelected] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const images = list.data ?? [];
  const current = images[Math.min(selected, images.length - 1)];
  const free = LIMIT - images.length;
  const set = (next: VehicleImage[]) => client.setQueryData(key, next);

  const order = useMutation({
    mutationFn: async (ids: string[]) => (await api<{ data: VehicleImage[] }>('POST', `/fleet/vehicles/${vehicleId}/images/order`, { ids })).data,
    onSuccess: set,
  });
  const remove = useMutation({
    mutationFn: async (imageId: string) => (await api<{ data: VehicleImage[] }>('DELETE', `/fleet/vehicles/${vehicleId}/images/${imageId}`)).data,
    onSuccess: (next) => {
      set(next);
      setSelected(0);
    },
  });

  async function upload(files: File[]) {
    setErrors([]);
    setBusy(true);
    const problems: string[] = [];
    let latest: VehicleImage[] | null = null;
    for (const file of files.slice(0, Math.max(free, 0))) {
      if (file.size > PRODUCT_IMAGE_LIMITS.maxBytes) {
        problems.push(`${file.name}: größer als 2 MB.`);
        continue;
      }
      try {
        latest = (await apiUpload<{ data: VehicleImage[] }>(`/fleet/vehicles/${vehicleId}/images`, file)).data;
      } catch (e) {
        problems.push(`${file.name}: ${e instanceof ApiError ? e.message : 'Hochladen fehlgeschlagen.'}`);
      }
    }
    if (files.length > free) problems.push(`Nur ${Math.max(free, 0)} von ${files.length} Fotos übernommen, ein Fahrzeug hat höchstens ${LIMIT} Fotos.`);
    if (latest) set(latest);
    setErrors(problems);
    setBusy(false);
  }
  const move = (index: number, to: number) => {
    const ids = images.map((i) => i.id);
    const [item] = ids.splice(index, 1);
    ids.splice(to, 0, item!);
    order.mutate(ids);
    setSelected(to);
  };

  return (
    <CollapsibleCard title="Fotos" storageKey="fleet-photos">
      {current ? (
        <figure className="vehicle-photos__main">
          <img src={photoUrl(vehicleId, current.id)} alt={`${title}, Foto ${selected + 1}`} />
          <figcaption className="muted small">
            {selected === 0 ? 'Hauptfoto · ' : ''}{current.fileName} · {current.width} × {current.height} px · {sizeText(current.sizeBytes)}
          </figcaption>
        </figure>
      ) : (
        <div className="vehicle-photos__empty muted">{list.isLoading ? 'Wird geladen …' : 'Noch keine Fotos.'}</div>
      )}
      {images.length > 0 && (
        <ul className="vehicle-photos__list">
          {images.map((img, i) => (
            <li key={img.id} className={i === selected ? 'is-selected' : undefined}>
              <button type="button" className="vehicle-photos__thumb" onClick={() => setSelected(i)} aria-label={`Foto ${i + 1} ansehen`} aria-pressed={i === selected}>
                <img src={photoUrl(vehicleId, img.id)} alt="" loading="lazy" />
                {i === 0 && <span className="vehicle-photos__badge">Hauptfoto</span>}
              </button>
              {canWrite && (
                <div className="vehicle-photos__tools">
                  <button type="button" className="btn btn--secondary btn--sm" disabled={i === 0 || order.isPending} onClick={() => move(i, i - 1)} aria-label={`Foto ${i + 1} nach vorn`}>←</button>
                  <button type="button" className="btn btn--secondary btn--sm" disabled={i === images.length - 1 || order.isPending} onClick={() => move(i, i + 1)} aria-label={`Foto ${i + 1} nach hinten`}>→</button>
                  {i > 0 && <button type="button" className="btn btn--secondary btn--sm" disabled={order.isPending} onClick={() => move(i, 0)} aria-label={`Foto ${i + 1} als Hauptfoto`} title="Als Hauptfoto">★</button>}
                  <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm(`Foto „${img.fileName}“ löschen?`) && remove.mutate(img.id)} aria-label={`Foto ${i + 1} löschen`}>×</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
        <div className="vehicle-photos__upload">
          <label className={`btn btn--secondary${free <= 0 || busy ? ' is-disabled' : ''}`}>
            {busy ? 'Wird hochgeladen …' : 'Fotos hochladen'}
            <input
              type="file"
              accept="image/webp,image/png,image/jpeg"
              multiple
              className="sr-only"
              disabled={free <= 0 || busy}
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = '';
                if (files.length) void upload(files);
              }}
            />
          </label>
          <small className="muted">WebP empfohlen, auch PNG oder JPEG. Höchstens 2 MB je Foto und {LIMIT} Fotos, noch {Math.max(free, 0)} frei. Das erste Foto ist das Hauptfoto.</small>
        </div>
      )}
      {errors.length > 0 && (
        <div className="alert alert--error" role="alert">
          {errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}
      {(order.error || remove.error) && <div className="alert alert--error" role="alert">{(order.error ?? remove.error)!.message}</div>}
    </CollapsibleCard>
  );
}
